import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Profile.css';

const Profile = () => {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    bio: '',
    avatar_url: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        avatar_url: user.avatar_url || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put('/auth/profile', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        bio: formData.bio,
        avatar_url: formData.avatar_url,
      });

      const updatedUser = { ...user, ...response.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (setUser) setUser(updatedUser);

      setMessage({ type: 'success', text: t('profile.updateSuccess') });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || t('profile.updateFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: t('profile.passwordMismatch') });
      setLoading(false);
      return;
    }

    try {
      await api.put('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      setMessage({ type: 'success', text: t('profile.passwordChanged') });
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || t('profile.passwordFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page profile-page">
      <div className="container">
        <h1>{t('profile.title')}</h1>

        <div className="profile-tabs">
          <button
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('profile.profileInfo')}
          </button>
          <button
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            {t('profile.security')}
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}

        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="avatar-section">
              <div className="avatar-preview">
                {formData.avatar_url ? (
                  <img src={formData.avatar_url} alt="Avatar" />
                ) : (
                  <div className="avatar-placeholder">
                    {formData.first_name?.charAt(0)}
                    {formData.last_name?.charAt(0)}
                  </div>
                )}
              </div>
              <div className="avatar-info">
                <h3>
                  {formData.first_name} {formData.last_name}
                </h3>
                <p className="role-badge">{user?.role}</p>
                <p className="email">{formData.email}</p>
              </div>
            </div>

            <form onSubmit={handleProfileSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('profile.firstName')}</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('profile.lastName')}</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('profile.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled
                  className="disabled"
                />
                <small>{t('profile.emailCannotChange')}</small>
              </div>

              <div className="form-group">
                <label>{t('profile.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div className="form-group">
                <label>{t('profile.avatarUrl')}</label>
                <input
                  type="url"
                  name="avatar_url"
                  value={formData.avatar_url}
                  onChange={handleChange}
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>

              <div className="form-group">
                <label>{t('profile.bio')}</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows="4"
                  placeholder={t('profile.bioPlaceholder')}
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? t('profile.saving') : t('profile.saveChanges')}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="security-section">
            <h2>{t('profile.changePassword')}</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>{t('profile.currentPassword')}</label>
                <input
                  type="password"
                  name="current_password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('profile.newPassword')}</label>
                <input
                  type="password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  minLength="6"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('profile.confirmNewPassword')}</label>
                <input
                  type="password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  minLength="6"
                  required
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? t('profile.changing') : t('profile.changePassword')}
              </button>
            </form>

            <div className="security-info">
              <h3>{t('profile.accountSecurity')}</h3>
              <p>
                <strong>{t('profile.lastLogin')}:</strong>{' '}
                {user?.last_login
                  ? new Date(user.last_login).toLocaleString()
                  : t('profile.na')}
              </p>
              <p>
                <strong>{t('profile.accountCreated')}:</strong>{' '}
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : t('profile.na')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
